import { useDica } from '../ui/Dica.js';

/**
 * Rosca com tooltip. Portado de `donut()` no protótipo.
 *
 * Sem biblioteca de gráficos, e isso é decisão do documento 05: os
 * cinco gráficos do protótipo são poucas dezenas de linhas de SVG cada.
 * Uma biblioteca traria peso, um segundo sistema de cores e um segundo
 * modelo de acessibilidade para substituir código que já existe e já
 * foi aprovado com o dono.
 *
 * A matemática que importa: `strokeDasharray` desenha um arco cuja
 * fração da circunferência é a fração do total. Os 3px descontados do
 * traço são o respiro entre fatias que o protótipo usa — sem eles as
 * cores encostam e a leitura piora.
 */

export interface FatiaDoDonut {
  readonly nome: string;
  readonly valor: number;
  readonly cor: string;
  /** O rótulo já formatado: "13,0 h", "R$ 46.100". */
  readonly rotulo: string;
  /** Corpo da dica. Aceita <br> e <em>. */
  readonly dica: string;
}

export function Donut({
  fatias,
  raio = 66,
  largura = 17,
  centro,
  legenda = true,
}: {
  fatias: readonly FatiaDoDonut[];
  raio?: number;
  largura?: number;
  /** O número grande no meio. */
  centro?: { valor: string; rotulo: string };
  legenda?: boolean;
}) {
  const dica = useDica();
  const tamanho = raio * 2 + largura + 8;
  const c = tamanho / 2;
  const circunferencia = 2 * Math.PI * raio;
  const total = fatias.reduce((soma, f) => soma + f.valor, 0);

  let acumulado = 0;

  return (
    <div>
      <div className="donut" style={{ width: tamanho, height: tamanho }}>
        <svg
          viewBox={`0 0 ${tamanho} ${tamanho}`}
          width={tamanho}
          height={tamanho}
          role="img"
          aria-label={fatias.map((f) => `${f.nome}: ${f.rotulo}`).join('; ')}
        >
          <circle
            cx={c}
            cy={c}
            r={raio}
            stroke="var(--panel-2)"
            strokeWidth={largura}
            fill="none"
          />
          {fatias.map((f) => {
            const comprimento = total > 0 ? circunferencia * (f.valor / total) : 0;
            const deslocamento = -acumulado;
            acumulado += comprimento;
            return (
              <circle
                key={f.nome}
                cx={c}
                cy={c}
                r={raio}
                stroke={f.cor}
                strokeWidth={largura}
                fill="none"
                strokeDasharray={`${Math.max(0, comprimento - 3)} ${circunferencia - comprimento + 3}`}
                strokeDashoffset={deslocamento}
                strokeLinecap="round"
                onMouseEnter={(e) => dica.mostrar(e, { titulo: f.nome, texto: f.dica })}
                onMouseMove={(e) => dica.mover(e)}
                onMouseLeave={() => dica.esconder()}
              />
            );
          })}
        </svg>
        {centro && (
          <div className="mid">
            <b className="num">{centro.valor}</b>
            <span>{centro.rotulo}</span>
          </div>
        )}
      </div>

      {legenda && (
        <ul className="legend">
          {fatias.map((f) => (
            <li
              key={f.nome}
              onMouseEnter={(e) => dica.mostrar(e, { titulo: f.nome, texto: f.dica })}
              onMouseMove={(e) => dica.mover(e)}
              onMouseLeave={() => dica.esconder()}
            >
              <i style={{ background: f.cor }} />
              {f.nome}
              <b className="num">{f.rotulo}</b>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
